import { computed, ref, watch } from 'vue'
import { createI18n, useI18n } from 'vue-i18n'
import en from './locales/en.js'

/**
 * Only the active locale is bundled into the entry chunk. The other is a
 * dynamic import fetched when the reader switches, which keeps ~4 kB gzip of
 * copy nobody is reading off the critical path.
 */
const LOADERS = { id: () => import('./locales/id.js') }

const MESSAGES = { en }

/** Bumped after a locale loads, so `useSection` recomputes. */
const revision = ref(0)

export const SUPPORTED_LOCALES = [
  { code: 'en', short: 'EN', nameKey: 'ui.language.en' },
  { code: 'id', short: 'ID', nameKey: 'ui.language.id' }
]

export const DEFAULT_LOCALE = 'en'

const STORAGE_KEY = 'omase.locale'

const isSupported = (value) => SUPPORTED_LOCALES.some((entry) => entry.code === value)

/**
 * Saved choice wins, then the browser's preference, then the default.
 * Storage access is wrapped because Safari private mode throws on read.
 */
function resolveInitialLocale() {
  if (typeof window === 'undefined') return DEFAULT_LOCALE

  try {
    const saved = window.localStorage.getItem(STORAGE_KEY)
    if (isSupported(saved)) return saved
  } catch {
    /* storage unavailable — fall through */
  }

  const preferred = (navigator.language || '').slice(0, 2).toLowerCase()
  return isSupported(preferred) ? preferred : DEFAULT_LOCALE
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: resolveInitialLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages: MESSAGES,
  // Structured section copy is read as raw objects, never as compiled
  // messages, so missing-key noise from those paths is not useful.
  missingWarn: false,
  fallbackWarn: false
})

/**
 * Fetches a locale's messages if they are not resident yet, then registers them
 * with vue-i18n. Safe to call repeatedly.
 */
export async function loadLocale(locale) {
  if (MESSAGES[locale] || !LOADERS[locale]) return
  const module = await LOADERS[locale]()
  MESSAGES[locale] = module.default
  i18n.global.setLocaleMessage(locale, module.default)
  revision.value += 1
}

/** Keeps `<html lang>` and the stored preference in step with the active locale. */
export function installLocalePersistence() {
  const apply = (locale) => {
    if (typeof document !== 'undefined') document.documentElement.setAttribute('lang', locale)
    try {
      window.localStorage.setItem(STORAGE_KEY, locale)
    } catch {
      /* storage unavailable — the page still works, the choice just is not kept */
    }
  }

  apply(i18n.global.locale.value)
  watch(i18n.global.locale, apply)
}

/**
 * Structured page copy (arrays of cards, tabs, steps…) read straight from the
 * locale module.
 *
 * `t()` compiles each string into a message function, which is right for
 * interpolated strings but forces `tm()` + `rt()` gymnastics on every nested
 * list. These sections are plain data, so reading the raw object keeps
 * components legible — same single source of truth, no duplication.
 *
 * @param {string} name key under `sections` in the locale files
 */
export function useSection(name) {
  const { locale } = useI18n()
  return computed(() => {
    revision.value // re-evaluate once a lazily fetched locale lands
    const active = MESSAGES[locale.value]?.sections?.[name]
    return active ?? MESSAGES[DEFAULT_LOCALE].sections[name]
  })
}
